import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { serviceService } from "../../../../services/api/serviceService";
import {
  getHasMoreServices,
  normalizeCategoriesResponse,
  normalizeServicesResponse,
  QUOTATION_SERVICE_PAGE_SIZE,
} from "../utils/catalogTransformers";

export const useServiceCatalog = (token_client) => {
  const observerTarget = useRef(null);
  const requestSeqRef = useRef(0);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [services, setServices] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categoryError, setCategoryError] = useState("");
  const [servicesError, setServicesError] = useState("");

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchCategories = useCallback(async () => {
    if (!token_client) return;

    try {
      setCategoriesLoading(true);
      setCategoryError("");

      const data = await serviceService.get_all_category(token_client);
      setCategories(normalizeCategoriesResponse(data));
    } catch (error) {
      console.error("Lỗi khi tải danh mục dịch vụ:", error);
      setCategoryError("Không thể tải danh mục dịch vụ");
      toast.error(error?.response?.data?.message || "Lỗi khi tải danh mục dịch vụ");
    } finally {
      setCategoriesLoading(false);
    }
  }, [token_client]);

  const fetchServices = useCallback(
    async (category, pageNumber) => {
      if (!token_client || !category?.id) return;

      const requestSeq = ++requestSeqRef.current;

      try {
        if (pageNumber === 1) setLoading(true);
        else setLoadingMore(true);
        setServicesError("");

        const params = {
          page: pageNumber,
          limit: QUOTATION_SERVICE_PAGE_SIZE,
          categoryId: category.id,
        };

        const data = await serviceService.get_list_user_service_short(token_client, params);
        if (requestSeq !== requestSeqRef.current) return;

        const transformed = normalizeServicesResponse(data, category);

        setServices((prev) => (pageNumber === 1 ? transformed : [...prev, ...transformed]));
        setHasMore(getHasMoreServices(data, transformed.length));
      } catch (error) {
        if (requestSeq !== requestSeqRef.current) return;

        console.error("Lỗi khi tải danh sách dịch vụ:", error);
        setServicesError("Không thể tải danh sách dịch vụ");
        toast.error(error?.response?.data?.message || "Lỗi khi tải danh sách dịch vụ");
      } finally {
        if (requestSeq === requestSeqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [token_client],
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCategoryChange = useCallback(
    (category) => {
      if (!category?.id || category.id === selectedCategory?.id) return;

      setSelectedCategory(category);
      setPage(1);
      setServices([]);
      setHasMore(false);
      fetchServices(category, 1);
    },
    [fetchServices, selectedCategory?.id],
  );

  useEffect(() => {
    if (selectedCategory?.id || categoriesLoading || categoryError) return;
    if (!categories.length) return;

    handleCategoryChange(categories[0]);
  }, [
    categories,
    categoriesLoading,
    categoryError,
    handleCategoryChange,
    selectedCategory?.id,
  ]);

  const handleObserver = useCallback(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && selectedCategory && hasMore && !loadingMore && !loading) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, loadingMore, loading, selectedCategory],
  );

  useEffect(() => {
    const element = observerTarget.current;
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "20px",
      threshold: 0,
    });
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver]);

  useEffect(() => {
    if (page > 1 && selectedCategory) fetchServices(selectedCategory, page);
  }, [fetchServices, page, selectedCategory]);

  const prependTempService = (service) => {
    setServices((prev) => [service, ...prev]);
  };

  return {
    categories,
    selectedCategory,
    categoriesLoading,
    categoryError,
    services,
    loading,
    loadingMore,
    hasMore,
    servicesError,
    onCategoryChange: handleCategoryChange,
    refetchCategories: fetchCategories,
    observerTarget,
    openAddModal,
    setOpenAddModal,
    prependTempService,
  };
};
